import React, {useContext, useState} from "react";
import {Box, FormControl, IconButton, InputLabel, MenuItem, Select, Slider, Stack, TextField, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Maximize01Icon} from "@hugeicons/core-free-icons";
import {menuItemsInfo} from "../editMenuItems";
import {PersonaModal} from "../../../../../components/modals/PersonaModal";
import {WebSettingsContext} from "../../../../../context/WebSettingsContext";
import {NodeType} from "../../../../../model/ModelData";
import {UI} from "../../uiTokens";
import {fieldSx, iconBtnSx} from "../editMenuStyles";
import {SectionHeader, SubSectionHeader, ToggleRow} from "../components/EditMenuSection";

export const LLMBoostTab = ({node, onUpdate, onChangeData}) => {
    const [expandPersona, setExpandPersona] = useState(false);
    const [expandParaphrazerPersona, setExpandParaphrazerPersona] = useState(false);
    const {modelIds} = useContext(WebSettingsContext);

    const {primary, secondary} = menuItemsInfo.llm_boost;
    const isQaOrRec = [NodeType.QA, NodeType.RECOMMENDATION].includes(node?.type);

    return (
        <>
            <PersonaModal
                handleClose={() => setExpandPersona(false)}
                open={expandPersona}
                onSuccess={(content) => {
                    onChangeData("system_prompt", content);
                    const state = {...node};
                    state.data["system_prompt"] = content;
                    onUpdate({...state});
                }}
                text={node?.data?.system_prompt}
            />
            <PersonaModal
                handleClose={() => setExpandParaphrazerPersona(false)}
                open={expandParaphrazerPersona}
                onSuccess={(content) => {
                    const state = {...node};
                    state.data["paraphrazer"] = content;
                    onUpdate({...state});
                }}
                text={node?.data?.paraphrazer}
            />

            <SectionHeader title={primary} description={secondary}/>

            {isQaOrRec && (
                <>
                    <Stack direction="row" useFlexGap flexWrap="wrap" sx={{columnGap: 2}}>
                        <ToggleRow
                            control="checkbox"
                            label="Paraphrazer"
                            checked={node?.data?.paraphrazer}
                            onChange={(e) => onChangeData("paraphrazer", e.target.checked)}
                            onBlur={() => onUpdate(node)}
                        />
                        <ToggleRow
                            control="checkbox"
                            label="Lemmatizer"
                            checked={node?.data?.lemmatizer}
                            onChange={(e) => onChangeData("lemmatizer", e.target.checked)}
                            onBlur={() => onUpdate(node)}
                        />
                    </Stack>

                    <Box>
                        <SubSectionHeader
                            title="Persona for Paraphrazer"
                            action={
                                <IconButton
                                    size="small"
                                    sx={iconBtnSx}
                                    disabled={!node?.data?.paraphrazer}
                                    onClick={() => setExpandParaphrazerPersona(true)}
                                >
                                    <HugeiconsIcon icon={Maximize01Icon} size={15}/>
                                </IconButton>
                            }
                        />
                        <TextField
                            value={node?.data?.persona_paraphrazer}
                            size="small"
                            fullWidth
                            disabled={!node?.data?.paraphrazer}
                            placeholder="Describe persona"
                            multiline
                            minRows={3}
                            maxRows={9}
                            sx={{mt: 0.75, ...fieldSx}}
                            onChange={(e) => onChangeData("persona_paraphrazer", e.target.value)}
                            onBlur={() => onUpdate(node)}
                        />
                    </Box>
                </>
            )}

            <Box>
                <SubSectionHeader
                    title="System Prompt"
                    action={
                        <IconButton size="small" sx={iconBtnSx} onClick={() => setExpandPersona(true)}>
                            <HugeiconsIcon icon={Maximize01Icon} size={15}/>
                        </IconButton>
                    }
                />
                <TextField
                    value={node?.data?.system_prompt}
                    size="small"
                    fullWidth
                    placeholder="Describe persona"
                    multiline
                    minRows={3}
                    maxRows={9}
                    sx={{mt: 0.75, ...fieldSx}}
                    onClick={() => setExpandPersona(true)}
                    onBlur={() => onUpdate(node)}
                />
            </Box>

            <FormControl size="small" sx={fieldSx}>
                <InputLabel id="llm-boost-model-label">Model</InputLabel>
                <Select
                    labelId="llm-boost-model-label"
                    value={node?.data?.model || "none"}
                    disabled={!node?.data?.use_llm}
                    label="Model"
                    onChange={(e => onChangeData("model", e.target.value === "none" ? null : e.target.value))}
                    onBlur={() => onUpdate(node)}
                >
                    {modelIds?.map(model => (
                        <MenuItem key={model} value={model}>{model}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{fontSize: 12.5, fontWeight: 600, color: UI.ink}}>Temperature</Typography>
                    <Typography sx={{fontSize: 11, color: UI.muted}}>{node?.data?.temp?.toFixed(2) || 0}</Typography>
                </Stack>
                <Slider
                    size="small"
                    sx={{color: UI.accent}}
                    value={node?.data?.temp || 0}
                    min={0} max={1} step={0.05}
                    onChange={e => onChangeData("temp", e.target.value)}
                    onBlur={() => onUpdate(node)}
                />
            </Box>

            <TextField
                type="number"
                value={node?.data?.conversation_buffer_window}
                size="small"
                label="Conversation buffer window"
                placeholder="Buffer window"
                sx={fieldSx}
                onChange={(e) => onChangeData("conversation_buffer_window", e.target.value)}
                onBlur={() => onUpdate(node)}
            />

            <ToggleRow
                control="checkbox"
                label="Share Conversation Context"
                checked={node?.data?.share_conversation_context}
                onChange={(e) => onChangeData("share_conversation_context", e.target.checked)}
                onBlur={() => onUpdate(node)}
            />

            <TextField
                type="number"
                value={node?.data?.contexts}
                size="small"
                InputLabelProps={{shrink: true}}
                label="Number of Extracted Contexts"
                placeholder="Number of Extracted Contexts"
                defaultValue={8}
                sx={fieldSx}
                onChange={(e) => onChangeData("contexts", e.target.value)}
                onBlur={() => onUpdate(node)}
            />
        </>
    );
};
